import React from 'react';

/**
 * Interface for KPI content components with static properties
 */
interface KpiComponent extends React.FC {
  title?: string;
  kpiHyperlink?: string;
  isDisclaimerRequired?: boolean;
  actionTitle?: string;
  actionHyperlink?: string;
  actionContent?: React.FC;
  actionDrawerWidth?: number;
}

/**
 * Helper function to create KPI content map entries
 * Handles type assertions and defaults in one place
 */
const createKpiEntry = (component: React.FC) => ({
  component,
  title: (component as KpiComponent).title,
  kpiHyperlink: (component as KpiComponent).kpiHyperlink ?? '',
  isDisclaimerRequired: (component as KpiComponent).isDisclaimerRequired ?? false,
  actionTitle: (component as KpiComponent).actionTitle ?? '',
  actionHyperlink: (component as KpiComponent).actionHyperlink ?? '',
  actionContent: (component as KpiComponent).actionContent,
  actionDrawerWidth: (component as KpiComponent).actionDrawerWidth,
});

// Import B2B content components
import BTAC from '../pages/view-info-content/operations/B2B/BTAC';
import BTEA from '../pages/view-info-content/operations/B2B/BTEA';
import BTSP from '../pages/view-info-content/operations/B2B/BTSP';
import BTNS from '../pages/view-info-content/operations/B2B/BTNS';
import BTNA from '../pages/view-info-content/operations/B2B/BTNA';
import BTYG from '../pages/view-info-content/operations/B2B/BTYG';
import BTGD from '../pages/view-info-content/operations/B2B/BTGD';
import BTES from '../pages/view-info-content/operations/B2B/BTES';

// Import Marketing content components
import AH08 from '../pages/view-info-content/operations/Marketing/AH08';
import ARCT from '../pages/view-info-content/operations/Marketing/ARCT';
import ARW1 from '../pages/view-info-content/operations/Marketing/ARW1';
import ARW2 from '../pages/view-info-content/operations/Marketing/ARW2';
import AHF from '../pages/view-info-content/operations/Marketing/AHF';
import MAPA from '../pages/view-info-content/operations/Marketing/MAPA';
import MAPE from '../pages/view-info-content/operations/Marketing/MAPE';
import MAPD from '../pages/view-info-content/operations/Marketing/MAPD';
import MPTC from '../pages/view-info-content/operations/Marketing/MPTC';
import SHRW from '../pages/view-info-content/operations/Marketing/SHRW';
import SO01 from '../pages/view-info-content/operations/Marketing/SO01';
import SO02 from '../pages/view-info-content/operations/Marketing/SO02';
import SO03 from '../pages/view-info-content/operations/Marketing/SO03';
import SO04 from '../pages/view-info-content/operations/Marketing/SO04';
import OPTI from '../pages/view-info-content/operations/Marketing/OPTI';
import BOPI from '../pages/view-info-content/operations/Marketing/BOPI';

// Import People content components
import BDGC from '../pages/view-info-content/operations/People/BDGC';
import EE from '../pages/view-info-content/operations/People/EE';
import OATT from '../pages/view-info-content/operations/People/OATT';
import OSAT from '../pages/view-info-content/operations/People/OSAT';
import SARD from '../pages/view-info-content/operations/People/SARD';
import SFTH from '../pages/view-info-content/operations/People/SFTH';
import TSRU from '../pages/view-info-content/operations/People/TSRU';
import HRAS from '../pages/view-info-content/operations/People/HRAS';
import EEYC from '../pages/view-info-content/operations/People/EEYC';
import SSKU from '../pages/view-info-content/operations/People/SSKU';
import PSHR from '../pages/view-info-content/operations/People/PSHR';
import OSSC from '../pages/view-info-content/operations/People/OSSC';
// Import Inventory content components
import BBPP from '../pages/view-info-content/operations/Inventory/BBPP';
import CRPC from '../pages/view-info-content/operations/Inventory/CRPC';
import DA from '../pages/view-info-content/operations/Inventory/DA';
import EOQMS from '../pages/view-info-content/operations/Inventory/EOQMS';
import INST from '../pages/view-info-content/operations/Inventory/INST';
import MGAC from '../pages/view-info-content/operations/Inventory/MGAC';
import MGIR from '../pages/view-info-content/operations/Inventory/MGIR';
import MGIS from '../pages/view-info-content/operations/Inventory/MGIS';
import MGIT from '../pages/view-info-content/operations/Inventory/MGIT';
import MGMA from '../pages/view-info-content/operations/Inventory/MGMA';
import MGOB from '../pages/view-info-content/operations/Inventory/MGOB';
import MGOS from '../pages/view-info-content/operations/Inventory/MGOS';
import MGUP from '../pages/view-info-content/operations/Inventory/MGUP';
import MOPC from '../pages/view-info-content/operations/Inventory/MOPC';
import PEFF from '../pages/view-info-content/operations/Inventory/PEFF';
import CPI7 from '../pages/view-info-content/operations/Inventory/CPI7';

// Import Performance content components
import OP_BREAKDOWN from '../pages/view-info-content/performance/OP_BREAKDOWN';
import AH01 from '../pages/view-info-content/performance/AH01';
import AH03 from '../pages/view-info-content/performance/AH03';
import AH07 from '../pages/view-info-content/performance/AH07';
import AH09 from '../pages/view-info-content/performance/AH09';
import ARMA from '../pages/view-info-content/performance/ARMA';
import CCL1 from '../pages/view-info-content/performance/CCL1';
import CCL3 from '../pages/view-info-content/performance/CCL3';
import CCLF from '../pages/view-info-content/performance/CCLF';
import TRTS from '../pages/view-info-content/performance/TRTS';
import SUPS from '../pages/view-info-content/performance/SUPS';
import LLMAX from '../pages/view-info-content/performance/LLMAX';
import YDND from '../pages/view-info-content/performance/YDND';

/**
 * KPI Code to Content Component Mapping
 * Maps KPI codes to their corresponding content components, titles, and hyperlinks
 */
export const kpiContentMap: Record<
  string,
  {
    component: React.FC;
    title?: string;
    kpiHyperlink?: string;
    isDisclaimerRequired?: boolean;
    actionTitle?: string;
    actionHyperlink?: string;
    actionContent?: React.FC;
    actionDrawerWidth?: number;
  }
> = {
  // B2B
  BTAC: createKpiEntry(BTAC),
  BTEA: createKpiEntry(BTEA),
  BTSP: createKpiEntry(BTSP),
  BTNS: createKpiEntry(BTNS),
  BTNA: createKpiEntry(BTNA),
  BTYG: createKpiEntry(BTYG),
  BTGD: createKpiEntry(BTGD),
  BTES: createKpiEntry(BTES),

  // Marketing
  AH08: createKpiEntry(AH08),
  ARCT: createKpiEntry(ARCT),
  ARW1: createKpiEntry(ARW1),
  ARW2: createKpiEntry(ARW2),
  AHF: createKpiEntry(AHF),
  MAPA: createKpiEntry(MAPA),
  MAPE: createKpiEntry(MAPE),
  MAPD: createKpiEntry(MAPD),
  MPTC: createKpiEntry(MPTC),
  SHRW: createKpiEntry(SHRW),
  SO01: createKpiEntry(SO01),
  SO02: createKpiEntry(SO02),
  SO03: createKpiEntry(SO03),
  SO04: createKpiEntry(SO04),
  OPTI: createKpiEntry(OPTI),
  BOPI: createKpiEntry(BOPI),
  BODF: createKpiEntry(BOPI),
  ASSM: createKpiEntry(BOPI),

  // People
  BDGC: createKpiEntry(BDGC),
  EE: createKpiEntry(EE),
  OATT: createKpiEntry(OATT),
  OSAT: createKpiEntry(OSAT),
  SARD: createKpiEntry(SARD),
  SFTH: createKpiEntry(SFTH),
  TSRU: createKpiEntry(TSRU),
  HRAS: createKpiEntry(HRAS),
  OSSC: createKpiEntry(OSSC),
  PSHR: createKpiEntry(PSHR),
  EEYC: createKpiEntry(EEYC),
  SSKU: createKpiEntry(SSKU),
  PWTT: createKpiEntry(SSKU),
  GATT: createKpiEntry(SSKU),
  PATT: createKpiEntry(SSKU),

  // Inventory
  BBPP: createKpiEntry(BBPP),
  CRPC: createKpiEntry(CRPC),
  DA: createKpiEntry(DA),
  EOQMS: createKpiEntry(EOQMS),
  INST: createKpiEntry(INST),
  MGAC: createKpiEntry(MGAC),
  MGIR: createKpiEntry(MGIR),
  MGIS: createKpiEntry(MGIS),
  MGIT: createKpiEntry(MGIT),
  MGMA: createKpiEntry(MGMA),
  MGOB: createKpiEntry(MGOB),
  MGOS: createKpiEntry(MGOS),
  MGUP: createKpiEntry(MGUP),
  MOPC: createKpiEntry(MOPC),
  PEFF: createKpiEntry(PEFF),
  CPI7: createKpiEntry(CPI7),

  // Performance
  OP_BREAKDOWN: createKpiEntry(OP_BREAKDOWN),
  AH01: createKpiEntry(AH01),
  AH03: createKpiEntry(AH03),
  AH07: createKpiEntry(AH07),
  AH09: createKpiEntry(AH09),
  ARMA: createKpiEntry(ARMA),
  CCL1: createKpiEntry(CCL1),
  CCL3: createKpiEntry(CCL3),
  CCLF: createKpiEntry(CCLF),
  TRTS: createKpiEntry(TRTS),
  SUPS: createKpiEntry(SUPS),
  LLMAX: createKpiEntry(LLMAX),
  YDND: createKpiEntry(YDND),
};
